import { Store } from 'pinia'
import { TranslateResult } from 'vue-i18n'
import { v4 as uuidv4 } from 'uuid'
import { reactive } from 'vue'

const { defineStore } = window.Pinia

enum MessageType {
    success = 'success',
    warning = 'warning',
    info = 'info',
    error = 'error',
}

export interface IMessage {
    getId(): string
    getMessage(): TranslateResult
    getType(): MessageType
    isRemoving(): boolean
    setIsRemoving(): this
}

class Message implements IMessage {
    private id: string
    private message: TranslateResult
    protected type: MessageType = MessageType.success
    private removing = false

    constructor (message: TranslateResult) {
        this.message = message
        this.id = uuidv4()
    }

    getId () {
        return this.id
    }

    getMessage () {
        return this.message
    }

    getType () {
        return this.type
    }

    isRemoving () {
        return this.removing
    }

    setIsRemoving (): this {
        this.removing = true
        return this
    }
}

export class SuccessMessage extends Message {
    constructor (message: TranslateResult) {
        super(message)
        this.type = MessageType.success
    }
}

export class WarningMessage extends Message {
    constructor (message: TranslateResult) {
        super(message)
        this.type = MessageType.warning
    }
}

export class ErrorMessage extends Message {
    constructor (message: TranslateResult) {
        super(message)
        this.type = MessageType.error
    }
}

export class InfoMessage extends Message {
    constructor (message: TranslateResult) {
        super(message)
        this.type = MessageType.info
    }
}

export interface IMessageList {
  [id: string]: Message
}

export interface State {
    list: IMessageList
}
interface ObjectMessage { message: string, type: keyof typeof MessageType}

export interface IStore {
    addMessage(message: IMessage | ObjectMessage): Promise<void>
    removeMessage(message: IMessage): Promise<void>
    getMessageList(): IMessageList
}

export interface IMessageStore extends Store<'message', IStore>{}

declare global {
    interface Window { vueMessagesStore: IMessageStore }
}

export default defineStore('messages', (): IStore => {
    const state: State = reactive({
        list: {},
    })

    const addMessage = async (message: Message | ObjectMessage) => {
        if (!(message instanceof Message)) {
            message = createMessage(message)
        }

        const id = message.getId()
        state.list[id] = message

        setTimeout(() => {
            removeMessage(state.list[id])
        }, 5000)
    }

    const createMessage = (message: ObjectMessage): Message => {
        switch (message.type) {
            case 'success':
                return new SuccessMessage(message.message)
            case 'error':
                return new ErrorMessage(message.message)
            case 'warning':
                return new WarningMessage(message.message)
            case 'info':
                return new InfoMessage(message.message)
        }
    }

    const removeMessage = async (message: IMessage) => {
        message.setIsRemoving()
        setTimeout(() => {
            delete state.list[message.getId()]
        }, 500)
    }

    const getMessageList = () => state.list

    return {
        addMessage,
        removeMessage,
        getMessageList,
    }
})
